import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { EVENTS, logEvent } from "../services/AnalyticsService";

const MediaPlaybackContext = createContext();

export const useMediaPlayback = () => {
  const context = useContext(MediaPlaybackContext);
  if (!context) {
    throw new Error("useMediaPlayback must be used within a MediaPlaybackProvider");
  }
  return context;
};

const getPageId = () => {
  const segments = window.location.pathname.split("/").filter(Boolean);
  return segments.includes("page") ? segments[segments.indexOf("page") + 1] : segments[0] || "demo";
};

export const MediaPlaybackProvider = ({ children }) => {
  const [playbackState, setPlaybackState] = useState({
    currentMediaId: null,
    currentModuleId: null,
    isPlaying: false,
  });
  const [giftBoxDepth, setGiftBoxDepth] = useState(0);

  const mediaElementsRef = useRef({});
  const moduleMediaRef = useRef({});
  const moduleTypeRef = useRef({});

  const registerMedia = useCallback((mediaId, element, moduleId, moduleType = "unknown") => {
    if (!element) return;

    mediaElementsRef.current[mediaId] = element;
    moduleTypeRef.current[moduleId] = moduleType;

    if (moduleId) {
      if (!moduleMediaRef.current[moduleId]) {
        moduleMediaRef.current[moduleId] = [];
      }
      if (!moduleMediaRef.current[moduleId].includes(mediaId)) {
        moduleMediaRef.current[moduleId].push(mediaId);
      }
    }
  }, []);

  const unregisterMedia = useCallback((mediaId, moduleId) => {
    if (mediaElementsRef.current[mediaId]) {
      mediaElementsRef.current[mediaId].pause();
      delete mediaElementsRef.current[mediaId];
    }

    if (moduleId && moduleMediaRef.current[moduleId]) {
      moduleMediaRef.current[moduleId] = moduleMediaRef.current[moduleId].filter((id) => id !== mediaId);
      if (moduleMediaRef.current[moduleId].length === 0) {
        delete moduleMediaRef.current[moduleId];
      }
    }

    setPlaybackState((previous) => (
      previous.currentMediaId === mediaId
        ? { currentMediaId: null, currentModuleId: null, isPlaying: false }
        : previous
    ));
  }, []);

  const requestPlay = useCallback(({ mediaId, mediaEl, moduleId, mediaType = "audio" }) => {
    Object.keys(mediaElementsRef.current).forEach((id) => {
      if (id !== mediaId && mediaElementsRef.current[id]) {
        try {
          mediaElementsRef.current[id].pause();
        } catch {
          // Ignore media elements that cannot be paused.
        }
      }
    });

    if (!mediaEl) return;

    const pageId = getPageId();
    const moduleType = moduleTypeRef.current[moduleId] || "unknown";

    mediaEl.play()
      .then(() => {
        logEvent(pageId, EVENTS.MEDIA_PLAY, {
          moduleType,
          moduleId,
          mediaType,
          action: "play",
        });

        if (mediaType === "audio") {
          logEvent(pageId, EVENTS.AUDIO_PLAY, { moduleType, moduleId, action: "play_audio" });
        } else if (mediaType === "video") {
          logEvent(pageId, EVENTS.VIDEO_PLAY, { moduleType, moduleId, action: "play_video" });
        }
      })
      .catch((error) => {
        console.error("Playback error:", error);
        logEvent(pageId, EVENTS.MEDIA_ERROR, {
          moduleType,
          moduleId,
          mediaType,
          errorMessage: error.message,
        });
      });

    const onEnded = () => {
      logEvent(pageId, EVENTS.MEDIA_COMPLETE, {
        moduleType,
        moduleId,
        mediaType,
        durationSeconds: mediaEl.duration,
      });
      mediaEl.removeEventListener("ended", onEnded);
    };
    mediaEl.addEventListener("ended", onEnded);

    setPlaybackState({ currentMediaId: mediaId, currentModuleId: moduleId || null, isPlaying: true });
  }, []);

  const requestPause = useCallback(({ mediaId }) => {
    const element = mediaElementsRef.current[mediaId];
    if (!element) return;

    element.pause();
    const pageId = getPageId();
    const moduleId = playbackState.currentModuleId;
    const moduleType = moduleTypeRef.current[moduleId] || "unknown";

    logEvent(pageId, EVENTS.MEDIA_PAUSE, {
      moduleType,
      moduleId,
      mediaType: "media",
      action: "pause",
      currentTimeSeconds: element.currentTime,
    });

    setPlaybackState((previous) => (
      previous.currentMediaId === mediaId ? { ...previous, isPlaying: false } : previous
    ));
  }, [playbackState.currentModuleId]);

  const pauseModule = useCallback((moduleId) => {
    if (moduleMediaRef.current[moduleId]) {
      moduleMediaRef.current[moduleId].forEach((mediaId) => {
        const element = mediaElementsRef.current[mediaId];
        if (element) {
          try {
            element.pause();
          } catch {
            // Ignore media elements that cannot be paused.
          }
        }
      });
    }

    setPlaybackState((previous) => (
      previous.currentModuleId === moduleId
        ? { currentMediaId: null, currentModuleId: null, isPlaying: false }
        : previous
    ));
  }, []);

  const stopAll = useCallback(() => {
    Object.values(mediaElementsRef.current).forEach((element) => {
      if (element) {
        try {
          element.pause();
        } catch {
          // Ignore media elements that cannot be paused.
        }
      }
    });
    setPlaybackState({ currentMediaId: null, currentModuleId: null, isPlaying: false });
  }, []);

  const enterGiftBoxLayer = useCallback(() => {
    setGiftBoxDepth((previous) => previous + 1);
    stopAll();
  }, [stopAll]);

  const exitGiftBoxLayer = useCallback(() => {
    setGiftBoxDepth((previous) => Math.max(0, previous - 1));
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) stopAll();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopAll();
    };
  }, [stopAll]);

  return (
    <MediaPlaybackContext.Provider
      value={{
        playbackState,
        giftBoxDepth,
        registerMedia,
        unregisterMedia,
        requestPlay,
        requestPause,
        pauseModule,
        enterGiftBoxLayer,
        exitGiftBoxLayer,
        stopAll,
      }}
    >
      {children}
    </MediaPlaybackContext.Provider>
  );
};
